export interface Journalist {
  name: string;
  slug: string;
  role: string;
  avatar: string;
  bio: string;
  fullBio?: string;
  linkedin?: string;
  twitter?: string;
  expertise?: string[];
  previousWork?: string;
  credentials?: string[];
  citations?: { outlet: string; context: string }[];
  education?: string;
  joinedYear?: number;
}

export const journalists: Record<string, Journalist> = {
  "marcus-chen": {
    name: "Marcus Chen",
    slug: "marcus-chen",
    role: "Senior Markets Reporter",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Marcus",
    bio: "Marcus covers prediction markets and political forecasting. Previously at Reuters and The Information, he has been tracking the intersection of finance and forecasting since 2019.",
    fullBio: "Marcus Chen is NEXTPredict's Senior Markets Reporter, covering prediction markets, political forecasting, and the evolving intersection of finance and information aggregation.\n\nBefore joining NEXTPredict, Marcus spent four years at Reuters covering US financial markets and two years at The Information, where he broke several stories on the emerging prediction markets ecosystem. His reporting on the 2024 election markets was cited by major outlets including Bloomberg and The Wall Street Journal.\n\nMarcus holds a degree in Economics from Columbia University and is a regular speaker at forecasting and fintech conferences. He is based in New York City.",
    linkedin: "https://linkedin.com/in/",
    twitter: "https://x.com/",
    expertise: ["Political Forecasting", "Election Markets", "Market Analysis"],
    previousWork: "Reuters, The Information",
    credentials: ["6+ years covering financial markets", "Broke the 2024 election markets story at Reuters", "Regular panelist at prediction markets conferences"],
    citations: [
      { outlet: "Bloomberg", context: "Election prediction markets analysis" },
      { outlet: "The Wall Street Journal", context: "Polymarket coverage and market accuracy" },
      { outlet: "Financial Times", context: "US midterm forecasting methodology" },
    ],
    education: "BA Economics, Columbia University",
    joinedYear: 2024,
  },
  "elena-vargas": {
    name: "Elena Vargas",
    slug: "elena-vargas",
    role: "Crypto & DeFi Editor",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Elena",
    bio: "Elena specializes in blockchain-based prediction markets and decentralized finance. She joined NEXTPredict from CoinDesk, where she led crypto markets coverage.",
    fullBio: "Elena Vargas leads NEXTPredict's crypto and DeFi coverage, focusing on blockchain-based prediction platforms, on-chain analytics, and the regulatory landscape for decentralized markets.\n\nShe joined NEXTPredict from CoinDesk, where she spent three years leading their crypto markets desk. Elena's deep expertise in on-chain data analysis and DeFi protocol mechanics gives her a unique lens on the rapidly evolving blockchain prediction market space.\n\nElena's work has been featured in The Block, Decrypt, and DL News. She is a frequent guest on crypto-focused podcasts and webinars.",
    linkedin: "https://linkedin.com/in/",
    twitter: "https://x.com/",
    expertise: ["DeFi", "Blockchain Markets", "On-chain Analytics"],
    previousWork: "CoinDesk",
    credentials: ["3 years as CoinDesk crypto markets lead", "Specialist in on-chain prediction market analytics", "Fluent in Solidity and smart contract analysis"],
    citations: [
      { outlet: "The Block", context: "Polymarket on-chain volume analysis" },
      { outlet: "Decrypt", context: "DeFi prediction market ecosystem overview" },
    ],
    education: "MS Computer Science, Stanford University",
    joinedYear: 2025,
  },
  "james-okoro": {
    name: "James Okoro",
    slug: "james-okoro",
    role: "Regulation & Policy Correspondent",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=James",
    bio: "James reports on the regulatory landscape shaping prediction markets globally. A former Capitol Hill staffer, he brings deep policy expertise to every story.",
    fullBio: "James Okoro is NEXTPredict's Regulation and Policy Correspondent, covering the legal and regulatory frameworks shaping the prediction markets industry in the US and globally.\n\nBefore joining NEXTPredict, James served as a legislative aide on Capitol Hill, working on financial services policy. He then moved to Bloomberg Law, where he covered derivatives regulation and the CFTC's evolving stance on event contracts.\n\nJames's insider perspective on Washington and his deep understanding of financial regulation make him a go-to source for analysis on how policy decisions will shape the prediction markets landscape. His reporting on the SEC's new event contract framework was cited by multiple major outlets.",
    linkedin: "https://linkedin.com/in/",
    twitter: "https://x.com/",
    expertise: ["CFTC Regulation", "Policy Analysis", "Global Compliance"],
    previousWork: "US Capitol Hill, Bloomberg Law",
    credentials: ["Former Capitol Hill legislative aide (financial services)", "4 years covering derivatives regulation at Bloomberg Law", "Source network across CFTC, SEC, and state regulators"],
    citations: [
      { outlet: "Reuters", context: "CFTC prediction market regulatory analysis" },
      { outlet: "Bloomberg Law", context: "Event contract framework commentary" },
      { outlet: "Politico", context: "Congressional prediction market legislation" },
    ],
    education: "JD, Georgetown University Law Center",
    joinedYear: 2024,
  },
  "sarah-kim": {
    name: "Sarah Kim",
    slug: "sarah-kim",
    role: "Tech & AI Reporter",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Sarah",
    bio: "Sarah covers artificial intelligence, automated trading, and the tech infrastructure behind modern prediction markets. Previously at Wired and MIT Technology Review.",
    fullBio: "Sarah Kim covers the intersection of artificial intelligence, automated trading systems, and the technology infrastructure powering modern prediction markets.\n\nBefore NEXTPredict, Sarah spent two years at Wired covering AI and machine learning applications in finance, and three years at MIT Technology Review, where she reported on algorithmic trading and forecasting systems.\n\nSarah's technical background allows her to go deeper than surface-level AI coverage, explaining how language models, calibration techniques, and automated systems are reshaping how prediction markets operate. Her reporting on AI trading bots in prediction markets was widely cited across tech media.",
    linkedin: "https://linkedin.com/in/",
    twitter: "https://x.com/",
    expertise: ["AI Trading", "Machine Learning", "Forecasting Tech"],
    previousWork: "Wired, MIT Technology Review",
    credentials: ["5 years covering AI in financial applications", "Technical background in machine learning", "Regular contributor to AI and fintech conferences"],
    citations: [
      { outlet: "MIT Technology Review", context: "AI-powered prediction market trading" },
      { outlet: "Wired", context: "Automated forecasting systems analysis" },
    ],
    education: "BS Computer Science, MIT",
    joinedYear: 2025,
  },
  "david-powell": {
    name: "David Powell",
    slug: "david-powell",
    role: "Culture & Entertainment Writer",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=David",
    bio: "David explores how prediction markets intersect with pop culture, entertainment, and sports. He brings a sharp analytical eye to the lighter side of forecasting.",
    fullBio: "David Powell covers the intersection of prediction markets with pop culture, entertainment, and sports, bringing analytical rigor to the lighter side of forecasting.\n\nDavid joined NEXTPredict from The Ringer, where he spent three years covering sports analytics and cultural trends. Before that, he was a features writer at Vox Media covering the gamification of media and entertainment.\n\nHis unique perspective bridges the gap between data-driven forecasting and mainstream culture, making prediction markets accessible to a broader audience. David's coverage of entertainment prediction markets, including awards show forecasting and sports contracts, has helped expand NEXTPredict's readership beyond the traditional finance audience.",
    linkedin: "https://linkedin.com/in/",
    twitter: "https://x.com/",
    expertise: ["Entertainment Markets", "Sports Forecasting", "Cultural Trends"],
    previousWork: "The Ringer, Vox Media",
    credentials: ["3 years covering sports analytics at The Ringer", "Features writer at Vox Media", "Specialist in cultural trend forecasting"],
    citations: [
      { outlet: "The Ringer", context: "Sports prediction market analysis" },
      { outlet: "Vox", context: "Cultural impact of prediction markets" },
    ],
    education: "BA Journalism, Northwestern University",
    joinedYear: 2025,
  },
  "anna-wright": {
    name: "Anna Wright",
    slug: "anna-wright",
    role: "Financial Markets Analyst",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Anna",
    bio: "Anna covers financial prediction markets, interest rates, and macroeconomic forecasting. She holds a CFA and previously worked at Bloomberg Intelligence.",
    fullBio: "Anna Wright is NEXTPredict's Financial Markets Analyst, covering interest rate prediction markets, macroeconomic forecasting, and the growing intersection of traditional finance and event contracts.\n\nAnna joined NEXTPredict from Bloomberg Intelligence, where she spent four years as a senior analyst covering derivatives and fixed income markets. She holds the CFA charter and brings institutional-grade analytical rigor to her coverage of financial prediction markets.\n\nHer analysis of Fed rate prediction markets has become a must-read for institutional traders, and her work on hedge fund prediction market strategies has been cited by multiple financial publications.",
    linkedin: "https://linkedin.com/in/",
    twitter: "https://x.com/",
    expertise: ["Interest Rates", "Macro Forecasting", "Financial Derivatives"],
    previousWork: "Bloomberg Intelligence",
    credentials: ["CFA Charterholder", "4 years as Bloomberg Intelligence senior analyst", "Specialist in derivatives and fixed income markets"],
    citations: [
      { outlet: "Bloomberg", context: "Fed rate prediction market analysis" },
      { outlet: "Financial Times", context: "Hedge fund prediction market strategies" },
      { outlet: "Barron's", context: "Interest rate forecasting methodology" },
    ],
    education: "MBA Finance, Wharton School of Business",
    joinedYear: 2024,
  },
};

// Map article slugs to journalist slugs
export const articleAuthors: Record<string, string> = {
  "prediction-markets-called-senate-race": "marcus-chen",
  "polymarket-1b-monthly-volume": "elena-vargas",
  "sec-framework-event-contracts": "james-okoro",
  "ai-models-trading-prediction-markets": "sarah-kim",
  "bitcoin-halving-contracts-2028": "elena-vargas",
  "oscars-predictable-markets": "david-powell",
  "fed-rate-prediction-markets": "anna-wright",
  "rise-of-prediction-markets": "marcus-chen",
  "european-regulators-prediction-markets": "james-okoro",
  "hedge-funds-prediction-market-desks": "anna-wright",
  "superforecasters-top-traders-polymarket": "marcus-chen",
  "prediction-markets-climate-policy": "james-okoro",
  "solana-vs-ethereum-prediction-markets": "elena-vargas",
  "gpt-5-launch-markets-pricing": "sarah-kim",
  "taylor-swift-tour-records-markets": "david-powell",
  "2026-midterm-primaries-signals": "marcus-chen",
};

export const getAuthor = (articleSlug: string, embeddedAuthor?: { name: string; slug: string; role?: string; avatar?: string }): Journalist | null => {
  // Prefer embedded Sanity author data
  if (embeddedAuthor) {
    return {
      name: embeddedAuthor.name,
      slug: embeddedAuthor.slug,
      role: embeddedAuthor.role ?? "",
      avatar: embeddedAuthor.avatar ?? `https://api.dicebear.com/9.x/notionists/svg?seed=${embeddedAuthor.name}`,
      bio: "",
    };
  }
  // Fall back to hardcoded map
  const authorSlug = articleAuthors[articleSlug];
  return authorSlug ? journalists[authorSlug] : null;
};
